import React from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import trLocale from 'date-fns/locale/tr';
import { formatDate, formatDateTime } from './Utils';

const toStr = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// `value` prop'u SADECE mui-datatables'ın filtre yolu için taşınır; DOM'a sızmaz
// (geçersiz HTML attribute uyarısı üretmesin diye children dışında hiçbir şey render edilmez).
const DateCell = ({ children }) => <>{children}</>;

export function dateFilterColumn(name, label, lang, options = {}) {
  // sort/sortKey: backend orderBy switch'iyle eşleme (parseTableParams `options.sortKey`i
  // gönderir); backend'in sıralayamadığı kolonda `sort: false` verilir.
  const { clientSide = false, includeTime = false, sort = true, sortKey } = options;
  // Client-side: actually filter the row in MUI DataTable. Return true to HIDE.
  // Server-side: rely on backend; logic returns false (never hides client-side).
  const clientLogic = (value, filters) => {
    if (!filters || !filters.length || !filters[0]) return false;
    const f = filters[0];
    if (!value) return true;
    const rowDate = new Date(value);
    if (Number.isNaN(rowDate.getTime())) return true;
    // Alt sınır DAHİL (seçilen günün 00:00'ı), üst sınır HARİÇ (ertesi günün 00:00'ı) — arka
    // ucun DateRangeFilter kuralıyla aynı. Eski `> T23:59:59` bitiş gününün son saniyesindeki
    // kaydı (23:59:59.500) dışarıda bırakıyordu. Gün sınırları tarayıcının yerel saatindedir.
    const day = (s) => new Date(s + 'T00:00:00');
    const nextDay = (s) => {
      const d = day(s);
      d.setDate(d.getDate() + 1);
      return d;
    };
    if (f.op === 'between') return rowDate < day(f.val[0]) || rowDate >= nextDay(f.val[1]);
    if (f.op === 'gte') return rowDate < day(f.val);
    if (f.op === 'lte') return rowDate >= nextDay(f.val);
    return false;
  };

  return {
    name,
    label,
    options: {
      filter: true,
      sort,
      ...(sortKey ? { sortKey } : {}),
      filterType: 'custom',
      // HÜCRE BİR ELEMENT DÖNDÜRÜR, DİZGE DEĞİL — bu bir biçim tercihi değil, mui-datatables
      // 4.3.0'ın filtre sözleşmesi: `computeDisplayRow` içinde
      //   f = "string" != typeof T && T ? (T.props && T.props.value ? T.props.value : f) : T
      // yani `customBodyRender` DİZGE dönerse `filterOptions.logic`'e HAM DEĞER değil O DİZGE
      // gider. Biçimlenmiş metin locale'e bağlıdır (`toLocaleDateString`: tr "31.12.2026",
      // en-US "12/31/2026"), `new Date(...)` onu Invalid Date okur ve clientSide mantığı
      // TÜM SATIRLARI gizler — filtre uygulanınca tablo boşalır. Element döndürüp ham değeri
      // `props.value`'da taşıyınca logic ISO değeri alır ve gerçekten süzer.
      // (Bedeli: bu kolonda serbest metin araması artık biçimlenmiş metni değil ISO değeri
      // görür; tarih kolonu DatePicker ile süzülüyor, metinle değil.)
      customBodyRender: (v) => (
        <DateCell value={v}>{includeTime ? formatDateTime(v, lang) : formatDate(v, lang)}</DateCell>
      ),
      customFilterListOptions: {
        render: (v) => {
          if (!v || !v[0]) return [];
          const f = v[0];
          if (f.op === 'between') return [`${label}: ${f.val[0]} — ${f.val[1]}`];
          if (f.op === 'gte') return [`${label} >= ${f.val}`];
          if (f.op === 'lte') return [`${label} <= ${f.val}`];
          return [];
        },
        update: (filterList, filterPos, index) => {
          filterList[index] = [];
          return filterList;
        },
      },
      filterOptions: {
        logic: clientSide ? clientLogic : () => false,
        display: (fl, onChange, i, col) => {
          const val = fl[i]?.[0];
          const startStr = val?.op === 'between' ? val.val?.[0] || '' : (val?.op === 'gte' ? val.val || '' : '');
          const endStr = val?.op === 'between' ? val.val?.[1] || '' : (val?.op === 'lte' ? val.val || '' : '');
          const applyFilter = (s, e) => {
            if (s && e) fl[i] = [{ op: 'between', val: [s, e] }];
            else if (s) fl[i] = [{ op: 'gte', val: s }];
            else if (e) fl[i] = [{ op: 'lte', val: e }];
            else fl[i] = [];
            onChange(fl[i], i, col);
          };
          return (
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={trLocale}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <DatePicker
                  label={`${label} (min)`}
                  value={startStr ? new Date(startStr + 'T00:00:00') : null}
                  onChange={(d) => applyFilter(toStr(d), endStr)}
                  slotProps={{ textField: { variant: 'standard', size: 'small', fullWidth: true } }}
                />
                <span style={{ color: '#A0A8B4' }}>—</span>
                <DatePicker
                  label={`${label} (max)`}
                  value={endStr ? new Date(endStr + 'T00:00:00') : null}
                  onChange={(d) => applyFilter(startStr, toStr(d))}
                  slotProps={{ textField: { variant: 'standard', size: 'small', fullWidth: true } }}
                />
              </div>
            </LocalizationProvider>
          );
        },
      },
    },
  };
}
